import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { ContactBillToFormPanel } from '@/components/organisms/contacts/ContactBillToFormPanel';
import { useBillTo } from '@/hooks/contacts/useBillTo';
import { useContact } from '@/hooks/contacts/useContact';
import type { BillTo } from '@/types/contact';

export const Route = createFileRoute(
  '/_dashboard/dashboard/contacts/$contactId/bill-to/$billToId/edit',
)({
  component: EditBillToPage,
});

function EditBillToPage() {
  const { contactId, billToId } = Route.useParams();
  const navigate = useNavigate();
  const contactQuery = useContact(contactId);
  const billToQuery = useBillTo(contactId);
  const billToEntries: BillTo[] = Array.isArray(billToQuery.data) ? billToQuery.data : [];
  const billTo = billToEntries.find((entry) => entry.id === billToId) ?? null;

  function navigateToContact() {
    void navigate({
      to: '/dashboard/contacts/$contactId',
      params: { contactId },
    });
  }

  if (contactQuery.isError && contactQuery.error?.status === 404) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground" />
        <div>
          <p className="font-semibold">Contact not found</p>
          <p className="text-sm text-muted-foreground">The contact may have been deleted.</p>
        </div>
        <Button onClick={() => navigate({ to: '/dashboard/contacts' })}>Back to Contacts</Button>
      </div>
    );
  }

  if (!billToQuery.isLoading && !billTo) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground" />
        <div>
          <p className="font-semibold">Bill-to entry not found</p>
          <p className="text-sm text-muted-foreground">It may have been deleted.</p>
        </div>
        <Button onClick={navigateToContact}>Back to Contact</Button>
      </div>
    );
  }

  return (
    <ContactBillToFormPanel
      mode="edit"
      contactId={contactId}
      currentContact={contactQuery.data}
      billTo={billTo}
      entriesCount={billToEntries.length}
      isLoading={contactQuery.isLoading || billToQuery.isLoading}
      onCancel={navigateToContact}
      onSaved={navigateToContact}
    />
  );
}
